/*
 * Phase 46 · Feb 2026 — PWA helpers.
 * ------------------------------------------------------------------
 * Two exports:
 *   - `registerServiceWorker()`: idempotent registration, safe to
 *     call from `index.js`. HTTPS or localhost only.
 *   - `PwaInstallController`: minimal event-bus + storage guard used
 *     by <PwaInstallBanner/> to decide when to prompt the user.
 */

const SW_URL = '/service-worker.js';
const SNOOZE_KEY = 'nymia_pwa_install_snooze_until';
const SNOOZE_DAYS = 7;
// Phase 47 · iOS tutorial has its own cooldown so the user can dismiss
// the banner without immediately hiding the tutorial too, and vice
// versa. Same 7-day window, tracked separately in localStorage.
const IOS_TUTORIAL_SNOOZE_KEY = 'nymia_ios_tutorial_snooze_until';
const IOS_TUTORIAL_SNOOZE_DAYS = 7;

/**
 * Register the service worker. Safe to call anytime — the browser
 * dedupes registrations. Errors are logged only in DEV.
 */
export const registerServiceWorker = () => {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (window.location.protocol !== 'https:' && !isLocalhost) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register(SW_URL, { scope: '/' })
      .then((reg) => {
        // Force an update check on every load; useful right after a
        // deploy so the client picks up new hashed static asset paths.
        reg.update?.().catch(() => {});
      })
      .catch((err) => {
        // Non-fatal — the app must keep working even without the SW.
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Nymia PWA] SW registration failed:', err);
        }
      });
  });
};

/**
 * Runtime helpers consumed by <PwaInstallBanner/>.
 */
export const pwa = {
  isStandalone() {
    if (typeof window === 'undefined') return false;
    // iOS Safari flag + generic PWA media query.
    return (
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      window.navigator.standalone === true
    );
  },
  isIos() {
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPad|iPod/.test(navigator.userAgent || '');
  },
  isIosSafari() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isIos = /iPhone|iPad|iPod/.test(ua);
    const isOtherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|GSA\//.test(ua);
    const isInApp = /FBAN|FBAV|FB_IAB|Instagram|TikTok|BytedanceWebview|Snapchat|Line\/|MicroMessenger|Twitter/.test(ua);
    return isIos && /Safari/.test(ua) && !isOtherBrowser && !isInApp;
  },
  isSnoozed() {
    try {
      const until = Number(window.localStorage.getItem(SNOOZE_KEY) || 0);
      return until && Date.now() < until;
    } catch (_e) { return false; }
  },
  snooze() {
    try {
      const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(SNOOZE_KEY, String(until));
    } catch (_e) { /* ignore */ }
  },
  /**
   * True when the current URL is a "critical flow" where the install
   * prompt would be disruptive (checkout, live active, etc).
   * A conservative allow-list keeps this obvious.
   */
  isOnCriticalRoute() {
    if (typeof window === 'undefined') return false;
    const p = window.location.pathname || '';
    if (p.startsWith('/checkout')) return true;
    if (p.startsWith('/lives/')) return true; // Live active or joining
    if (p.startsWith('/o2o/')) return true;   // One-to-One session live
    if (p.startsWith('/creator/onboarding')) return true; // Onboarding wizard
    return false;
  },
  // ------------------------------------------------------------------
  // Phase 47 · iOS tutorial cooldown (separate from banner snooze).
  // ------------------------------------------------------------------
  isIosTutorialSnoozed() {
    try {
      const until = Number(window.localStorage.getItem(IOS_TUTORIAL_SNOOZE_KEY) || 0);
      return until && Date.now() < until;
    } catch (_e) { return false; }
  },
  snoozeIosTutorial() {
    try {
      const until = Date.now() + IOS_TUTORIAL_SNOOZE_DAYS * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(IOS_TUTORIAL_SNOOZE_KEY, String(until));
    } catch (_e) { /* ignore */ }
  },
};
